/**
 * Stakeholders List Route
 *
 * Displays searchable, filterable list of stakeholders with navigation
 */

import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useListPageState } from '../../hooks/useListPageState';
import { useStakeholders } from '../../graphql/queries';
import {
  DataTable,
  EmptyState,
  Pagination,
  PermissionGate,
  TableSkeleton,
  type DataTableColumn,
} from '../../components/shared';
import type { Stakeholder, StakeholderType } from '../../graphql/types';
import '@tylertech/forge/text-field';
import '@tylertech/forge/select';
import '@tylertech/forge/button';
import '@tylertech/forge/icon';
import './index.css';

export const Route = createFileRoute('/stakeholders/')({
  component: StakeholdersListPage,
  validateSearch: (search: Record<string, unknown>) => ({
    page: Math.max(1, Number(search.page) || 1),
    pageSize: Number(search.pageSize) || 25,
  }),
});

// Export component for testing
export { StakeholdersListPage };

interface StakeholderFilters {
  search: string;
  stakeholderType: StakeholderType | '';
}

const STAKEHOLDER_TYPE_LABELS: Record<string, string> = {
  PartnerAgencyOwner: 'Partner Agency Owner',
  PartnerOwner: 'Partner Owner',
};

// Short intro shown under the page title (issue #1913) — Stakeholders are a
// Business-pillar reference record (external partner agency or partner
// owner), not a per-user list, so the description explains what they model
// and where they're referenced.
const STAKEHOLDERS_PAGE_DESCRIPTION =
  'A stakeholder is an external partner agency or partner owner referenced by ' +
  'Business Capabilities and Data Domains. Maintain this list to track who to ' +
  'contact about a capability, and which partners are affected by a change.';

const ForgeButton = 'forge-button' as React.ElementType;

function StakeholdersListPage(): JSX.Element {
  const navigate = useNavigate();
  const { page, pageSize } = Route.useSearch();

  // State for filters
  const [filters, setFilters] = useState<StakeholderFilters>({
    search: '',
    stakeholderType: '',
  });

  // Fetch stakeholders with GraphQL
  const { data, loading, error } = useStakeholders();

  // Extract stakeholders from connection
  const stakeholders = data?.stakeholders?.edges?.map((edge) => edge.node) || [];

  const { sortColumn, sortDirection, handleSort, pagedItems, totalCount } =
    useListPageState<Stakeholder>({
      items: stakeholders,
      page,
      pageSize,
      filterFn: (stakeholder) => {
        if (filters.search) {
          const searchLower = filters.search.toLowerCase();
          const matchesName = stakeholder.name.toLowerCase().includes(searchLower);
          const matchesDescription = stakeholder.description?.toLowerCase().includes(searchLower);
          if (!matchesName && !matchesDescription) {
            return false;
          }
        }
        if (filters.stakeholderType && stakeholder.stakeholderType !== filters.stakeholderType) {
          return false;
        }
        return true;
      },
      sortValue: (stakeholder, column) => String(stakeholder[column as keyof Stakeholder] ?? ''),
    });

  // DataTable columns
  const columns: DataTableColumn<Stakeholder>[] = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      width: '30%',
      render: (stakeholder) => (
        <div>
          <div className="entity-list-name-cell">{stakeholder.name}</div>
          {stakeholder.description && (
            <div className="entity-list-description-cell">
              {stakeholder.description.substring(0, 60)}
              {stakeholder.description.length > 60 ? '...' : ''}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'stakeholderType',
      label: 'Type',
      sortable: true,
      width: '20%',
      render: (stakeholder) => (
        <span>
          {STAKEHOLDER_TYPE_LABELS[stakeholder.stakeholderType] ?? stakeholder.stakeholderType}
        </span>
      ),
    },
    {
      key: 'organization',
      label: 'Organization',
      sortable: true,
      width: '25%',
      render: (stakeholder) => <span>{stakeholder.organization || '—'}</span>,
    },
    {
      key: 'contactEmail',
      label: 'Contact',
      width: '25%',
      render: (stakeholder) => (
        <div className="entity-list-truncate-cell">
          {stakeholder.contactEmail ? (
            <a href={`mailto:${stakeholder.contactEmail}`}>{stakeholder.contactEmail}</a>
          ) : (
            '—'
          )}
        </div>
      ),
    },
  ];

  const handleRowClick = (stakeholder: Stakeholder): void => {
    navigate({ to: `/stakeholders/${stakeholder.entityId}` });
  };

  const handleFilterChange = (newFilters: Partial<StakeholderFilters>): void => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    void navigate({ to: '/stakeholders', search: { page: 1, pageSize } });
  };

  const handleClearFilters = (): void => {
    setFilters({ search: '', stakeholderType: '' });
    void navigate({ to: '/stakeholders', search: { page: 1, pageSize } });
  };

  const handlePageChange = (newPage: number): void => {
    void navigate({ to: '/stakeholders', search: { page: newPage, pageSize } });
  };

  const handlePageSizeChange = (newPageSize: number): void => {
    void navigate({ to: '/stakeholders', search: { page: 1, pageSize: newPageSize } });
  };

  if (error) {
    return (
      <div className="stakeholders-list-page">
        <h1 className="forge-typography--headline4">Stakeholders</h1>
        <p className="forge-typography--body2 page-header-description">
          {STAKEHOLDERS_PAGE_DESCRIPTION}
        </p>
        <div className="entity-list-error-state">
          <EmptyState
            icon="error"
            title="Failed to load stakeholders"
            description={error.message}
            actionLabel="Retry"
            onAction={() => window.location.reload()}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="stakeholders-list-page">
      <div className="stakeholders-header">
        <div>
          <h1 className="forge-typography--headline4">Stakeholders</h1>
          <p className="forge-typography--body2 page-header-description">
            {STAKEHOLDERS_PAGE_DESCRIPTION}
          </p>
          <p className="forge-typography--body2 page-header-subtitle">
            {loading ? 'Loading...' : `${totalCount} stakeholders`}
          </p>
        </div>
        <PermissionGate permission="stakeholder:create">
          <ForgeButton
            type="button"
            variant="raised"
            onClick={() => navigate({ to: '/stakeholders/new' })}
          >
            Create Stakeholder
          </ForgeButton>
        </PermissionGate>
      </div>

      {/* Filters */}
      <div className="filters-row">
        <div className="search-field">
          <input
            type="text"
            className="search-input"
            placeholder="Search stakeholders..."
            aria-label="Search stakeholders"
            value={filters.search}
            onChange={(e) => handleFilterChange({ search: e.target.value })}
          />
        </div>

        <select
          className="filter-select"
          aria-label="Filter by stakeholder type"
          value={filters.stakeholderType}
          onChange={(e) =>
            handleFilterChange({ stakeholderType: e.target.value as StakeholderType | '' })
          }
        >
          <option value="">All Types</option>
          <option value="PartnerAgencyOwner">Partner Agency Owner</option>
          <option value="PartnerOwner">Partner Owner</option>
        </select>

        {(filters.search || filters.stakeholderType) && (
          <ForgeButton type="button" variant="outlined" onClick={handleClearFilters}>
            Clear Filters
          </ForgeButton>
        )}
      </div>

      {/* Data Table */}
      <div className="stakeholders-table-container">
        {loading ? (
          <TableSkeleton rows={10} columns={4} />
        ) : (
          <>
            <DataTable
              columns={columns}
              data={pagedItems}
              getRowKey={(stakeholder) => stakeholder.entityId}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
              onRowClick={handleRowClick}
              emptyState={{
                icon: 'inbox',
                title: 'No stakeholders found',
                description:
                  filters.search || filters.stakeholderType
                    ? 'Try adjusting your filters'
                    : 'Create your first stakeholder to get started',
                actionLabel: 'Create Stakeholder',
                onAction: () => navigate({ to: '/stakeholders/new' }),
              }}
            />
            <Pagination
              totalCount={totalCount}
              pageSize={pageSize}
              currentPage={page}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              pageSizeOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </div>
    </div>
  );
}
